/**
 * Auto Starter Dispute Wizard
 * 
 * A simplified, guided dispute flow for Auto Starter tier users.
 * Uses bureau_package strategy exclusively with standard templates only.
 * 
 * Supports two presentation styles:
 * - Factual: Plain language, clear errors focus
 * - Metro 2: Technical field-level analysis
 * 
 * Both use the SAME backend engine. The difference is only in UI presentation.
 * 
 * Steps:
 * 1. Choose Dispute Type (if not set in onboarding)
 * 2. Review Detected Issues
 * 3. Add Dispute Context
 * 4. Summary Before Generation
 * 5. Generated Preview
 * 6. Finalize / Download / Status
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useReportData } from '../../context/ReportDataContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { metro2Service, ROUND_STATUS_INFO } from '../../services/metro2Service';
import { formatDate } from '../../utils/dateTime';
import {
  filterNegativeTradelines,
  getNegativeDisplayReason,
} from '../../utils/negativeAccountClassifier';
import {
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  CheckCircle,
  FileText,
  Download,
  Eye,
  Loader2,
  Building2,
  CreditCard,
  Search,
  User,
  Info,
  Check,
  X,
  Database,
  Scale,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

// Bureau colors
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Experian: 'bg-amber-100 text-amber-700 border-amber-200',
  Equifax: 'bg-red-100 text-red-700 border-red-200',
};

// Dispute type info
const DISPUTE_TYPES = {
  factual: {
    name: 'Factual Dispute',
    icon: FileText,
    description: 'Clear, easy-to-understand disputes focused on reporting errors',
    color: 'emerald',
  },
  metro2: {
    name: 'Metro 2 Dispute',
    icon: Database,
    description: 'Technical disputes with field-level Metro 2 analysis',
    color: 'emerald',
  },
};

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps, stepLabels }) => {
  return (
    <div className="flex items-center justify-center mb-8" data-testid="wizard-step-indicator">
      {stepLabels.map((label, index) => (
        <React.Fragment key={index}>
          <div className="flex flex-col items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                index + 1 < currentStep
                  ? 'bg-emerald-500 text-white'
                  : index + 1 === currentStep
                  ? 'bg-emerald-600 text-white ring-4 ring-emerald-100'
                  : 'bg-stone-200 text-stone-500'
              }`}
            >
              {index + 1 < currentStep ? <Check size={18} /> : index + 1}
            </div>
            <span className={`text-xs mt-2 max-w-[80px] text-center ${
              index + 1 === currentStep ? 'text-emerald-700 font-medium' : 'text-stone-500'
            }`}>
              {label}
            </span>
          </div>
          {index < totalSteps - 1 && (
            <div
              className={`w-12 h-1 mx-2 rounded ${
                index + 1 < currentStep ? 'bg-emerald-500' : 'bg-stone-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Issue card component for Step 1
const IssueCard = ({ item, type, isSelected, onToggle }) => {
  const bureauColor = BUREAU_COLORS[item.bureau] || 'bg-stone-100 text-stone-700';
  const negativeReason = type === 'account' ? getNegativeDisplayReason(item) : null;
  
  return (
    <div
      className={`border rounded-xl p-4 transition-all cursor-pointer ${
        isSelected
          ? 'border-emerald-500 bg-emerald-50 shadow-sm'
          : 'border-stone-200 hover:border-stone-300 bg-white'
      }`}
      onClick={onToggle}
      data-testid={`issue-card-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggle}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {type === 'account' ? (
              <CreditCard size={16} className="text-stone-500" />
            ) : (
              <Search size={16} className="text-stone-500" />
            )}
            <span className="font-semibold text-stone-900 truncate">
              {item.creditor_name || item.creditorName || 'Unknown Creditor'}
            </span>
            <Badge variant="outline" className={`text-xs ${bureauColor}`}>
              {item.bureau || 'Unknown'}
            </Badge>
          </div>
          
          {/* Issue summary */}
          <div className="text-sm text-stone-600 space-y-1">
            {negativeReason && (
              <p className="text-red-600 font-medium">{negativeReason}</p>
            )}
            {item.is_collection && !negativeReason && (
              <p className="text-amber-600 font-medium">Collection Account</p>
            )}
            {item.is_charge_off && !negativeReason && (
              <p className="text-red-600 font-medium">Charge-Off</p>
            )}
            {item.negative_reasons && item.negative_reasons.length > 0 && (
              <p className="text-stone-500">
                Issues: {item.negative_reasons.slice(0, 2).join(', ')}
                {item.negative_reasons.length > 2 && ` +${item.negative_reasons.length - 2} more`}
              </p>
            )}
            {item.balance && (
              <p>Balance: ${Number(item.balance).toLocaleString()}</p>
            )}
            {type === 'inquiry' && item.inquiry_date && (
              <p>Inquiry Date: {item.inquiry_date}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main wizard component
const DisputeWizardPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, getAuthHeaders } = useAuth();
  const { reportData } = useReportData();
  const { state: onboardingState } = useOnboarding();
  
  // Dispute type from onboarding or URL param
  const initialDisputeType = searchParams.get('type') || onboardingState?.dispute_type || null;
  
  // Wizard state - if dispute type already set, skip step 0
  const [currentStep, setCurrentStep] = useState(initialDisputeType ? 1 : 0);
  const [disputeType, setDisputeType] = useState(initialDisputeType);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(true);
  
  // Data state
  const [negativeAccounts, setNegativeAccounts] = useState([]);
  const [hardInquiries, setHardInquiries] = useState([]);
  const [selectedItems, setSelectedItems] = useState({ accounts: [], inquiries: [] });
  const [userContext, setUserContext] = useState({
    customNote: '',
    recognitionStatus: {},
    personalInfoCorrection: '',
  });
  
  // Generated data
  const [analysisResult, setAnalysisResult] = useState(null);
  const [roundData, setRoundData] = useState(null);
  const [generatedLetters, setGeneratedLetters] = useState([]);
  const [letterContent, setLetterContent] = useState(null);
  
  // Step labels based on whether we need dispute type selection
  const stepLabels = disputeType 
    ? ['Review', 'Context', 'Summary', 'Preview', 'Complete']
    : ['Type', 'Review', 'Context', 'Summary', 'Preview', 'Complete'];
  
  const totalSteps = stepLabels.length;
  
  // Get current dispute type info
  const disputeTypeInfo = disputeType ? DISPUTE_TYPES[disputeType] : null;
  
  // Load negative accounts and inquiries
  useEffect(() => {
    loadDisputeableItems();
  }, [reportData]);
  
  const loadDisputeableItems = async () => {
    setAnalyzing(true);
    try {
      // Extract negative accounts from report data
      const accounts = filterNegativeTradelines(reportData?.tradelines || []);
      const inquiries = reportData?.inquiries?.filter(i => i.inquiry_type === 'hard') || [];
      
      setNegativeAccounts(accounts.map(a => ({
        ...a,
        id: a.tradeline_id || a.id,
        creditor_name: a.creditor_name || a.creditorName,
      })));
      
      setHardInquiries(inquiries.map(i => ({
        ...i,
        id: i.inquiry_id || i.id,
        creditor_name: i.creditor_name || i.creditorName,
      })));
      
    } catch (error) {
      console.error('Failed to load items:', error);
      toast.error('Failed to load dispute items');
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Toggle item selection
  const toggleItem = (type, id) => {
    setSelectedItems(prev => {
      const key = type === 'account' ? 'accounts' : 'inquiries';
      const current = prev[key];
      const newList = current.includes(id)
        ? current.filter(i => i !== id)
        : [...current, id];
      return { ...prev, [key]: newList };
    });
  };
  
  // Select all items
  const selectAll = () => {
    setSelectedItems({
      accounts: negativeAccounts.map(a => a.id),
      inquiries: hardInquiries.map(i => i.id),
    });
  };
  
  // Clear all selections
  const clearAll = () => {
    setSelectedItems({ accounts: [], inquiries: [] });
  };
  
  // Get bureau counts
  const getBureauCounts = () => {
    const counts = {};
    
    selectedItems.accounts.forEach(id => {
      const account = negativeAccounts.find(a => a.id === id);
      if (account?.bureau) {
        counts[account.bureau] = (counts[account.bureau] || 0) + 1;
      }
    });
    
    selectedItems.inquiries.forEach(id => {
      const inquiry = hardInquiries.find(i => i.id === id);
      if (inquiry?.bureau) {
        counts[inquiry.bureau] = (counts[inquiry.bureau] || 0) + 1;
      }
    });
    
    return counts;
  };
  
  const totalSelected = selectedItems.accounts.length + selectedItems.inquiries.length;
  const bureauCounts = getBureauCounts();
  const packageCount = Object.keys(bureauCounts).length;
  
  // Step 3: Create round and process
  const handleCreateRound = async () => {
    setLoading(true);
    try {
      // Build consumer data from report
      const consumerData = {
        full_name: reportData?.personal_info?.name || user?.name || '',
        ssn_last4: reportData?.personal_info?.ssn_last4 || '',
        date_of_birth: reportData?.personal_info?.dob || '',
        current_address: reportData?.personal_info?.current_address || 
          (reportData?.addresses?.[0]?.full_address) || '',
      };
      
      // Create round
      const createResult = await metro2Service.createRound({
        accountIds: selectedItems.accounts,
        inquiryIds: selectedItems.inquiries,
        userId: user?.user_id || user?.id,
        tier: 'auto_starter',
        strategyMode: 'bureau_package', // Auto Starter = bureau_package only
        provider: reportData?.provider || 'identityiq',
        reportData: {
          tradelines: negativeAccounts.filter(a => selectedItems.accounts.includes(a.id)),
          inquiries: hardInquiries.filter(i => selectedItems.inquiries.includes(i.id)),
        },
        consumerData,
      }, token);
      
      if (!createResult.success) {
        throw new Error(createResult.message || 'Failed to create round');
      }
      
      // Process round
      const processResult = await metro2Service.processRound(
        createResult.round_id,
        user?.user_id || user?.id,
        token
      );
      
      if (!processResult.success) {
        throw new Error(processResult.message || 'Failed to process round');
      }
      
      setRoundData({
        ...createResult,
        ...processResult,
      });
      
      setCurrentStep(4);
      
    } catch (error) {
      console.error('Create round error:', error);
      toast.error(error.message || 'Failed to create dispute round');
    } finally {
      setLoading(false);
    }
  };
  
  // Step 4: Generate letters
  const handleGenerateLetters = async () => {
    if (!roundData?.round_id) return;
    
    setLoading(true);
    try {
      const result = await metro2Service.renderLetters(
        roundData.round_id,
        'standard', // Auto Starter = standard only
        user?.user_id || user?.id,
        token
      );
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to generate letters');
      }
      
      setGeneratedLetters(result.packages || []);
      
      // Load first letter content for preview
      if (result.packages?.length > 0) {
        const firstPackage = result.packages[0];
        const letterResult = await metro2Service.getLetter(
          roundData.round_id,
          firstPackage.package_id,
          token
        );
        setLetterContent(letterResult);
      }
      
      toast.success(`Generated ${result.letters_rendered} dispute letter(s)`);
      
    } catch (error) {
      console.error('Generate letters error:', error);
      toast.error(error.message || 'Failed to generate letters');
    } finally {
      setLoading(false);
    }
  };
  
  // Finalize and go to step 5
  const handleFinalize = () => {
    setCurrentStep(5);
    toast.success('Dispute round finalized successfully!');
  };
  
  // Download letter
  const handleDownload = (packageData) => {
    if (!letterContent?.content) {
      toast.error('Letter content not available');
      return;
    }
    
    const blob = new Blob([letterContent.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute_letter_${packageData?.target_bureau || 'bureau'}_${roundData?.round_id?.slice(0, 8)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Letter downloaded');
  };
  
  // Navigate steps
  const goNext = () => {
    // Adjust step numbers based on whether we have dispute type step
    const actualStep = disputeType ? currentStep : currentStep;
    
    if (currentStep === 0) {
      // Dispute type selection step
      if (disputeType) {
        setCurrentStep(1);
      }
    } else if (disputeType ? currentStep === 2 : currentStep === 3) {
      handleCreateRound();
    } else if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // Handle dispute type selection
  const handleSelectDisputeType = (type) => {
    setDisputeType(type);
  };
  
  // Render step content
  const renderStepContent = () => {
    // If no dispute type set, show type selection first
    if (!disputeType && currentStep === 0) {
      return renderDisputeTypeSelection();
    }
    
    // Adjust step index if dispute type was pre-selected
    const effectiveStep = disputeType ? currentStep : currentStep - 1;
    
    switch (effectiveStep) {
      case 0:
        return renderDisputeTypeSelection();
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return null;
    }
  };
  
  // Step 0: Dispute Type Selection (if not already set)
  const renderDisputeTypeSelection = () => (
    <div className="space-y-6" data-testid="wizard-step-type">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-2">
          Choose your dispute approach
        </h2>
        <p className="text-stone-600 max-w-2xl mx-auto">
          Both options use our powerful dispute engine. Choose the presentation style that works best for you.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        {/* Factual Dispute */}
        <button
          onClick={() => handleSelectDisputeType('factual')}
          className={`text-left p-6 rounded-xl border-2 transition-all ${
            disputeType === 'factual'
              ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
              : 'border-stone-200 hover:border-emerald-300 hover:bg-emerald-50/50'
          }`}
          data-testid="dispute-type-factual"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              disputeType === 'factual' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-600'
            }`}>
              <FileText size={24} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-stone-900">Factual Dispute</h3>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  disputeType === 'factual' ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300'
                }`}>
                  {disputeType === 'factual' && <Check size={12} className="text-white" />}
                </div>
              </div>
              <p className="text-stone-600 text-sm mt-2">
                Best for users who want a clear, easy-to-understand dispute focused on errors, identity theft, unauthorized activity, and other reporting issues identified in their credit report.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="outline" className="text-xs">Plain language</Badge>
                <Badge variant="outline" className="text-xs">Clear errors focus</Badge>
                <Badge variant="outline" className="text-xs">Great for first-timers</Badge>
              </div>
            </div>
          </div>
        </button>
        
        {/* Metro 2 Dispute */}
        <button
          onClick={() => handleSelectDisputeType('metro2')}
          className={`text-left p-6 rounded-xl border-2 transition-all ${
            disputeType === 'metro2'
              ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
              : 'border-stone-200 hover:border-emerald-300 hover:bg-emerald-50/50'
          }`}
          data-testid="dispute-type-metro2"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              disputeType === 'metro2' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-600'
            }`}>
              <Database size={24} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-stone-900">Metro 2 Dispute</h3>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  disputeType === 'metro2' ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300'
                }`}>
                  {disputeType === 'metro2' && <Check size={12} className="text-white" />}
                </div>
              </div>
              <p className="text-stone-600 text-sm mt-2">
                Best for users who want a more technical, field-specific dispute built around reporting inconsistencies, data conflicts, and Metro 2-style analysis.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="outline" className="text-xs">Technical analysis</Badge>
                <Badge variant="outline" className="text-xs">Metro 2 fields</Badge>
                <Badge variant="outline" className="text-xs">Data consistency</Badge>
              </div>
            </div>
          </div>
        </button>
      </div>
      
      {/* Info box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Info size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-medium mb-1">Same powerful engine, different presentation</p>
          <p>Both options use the same dispute logic and generate equally strong letters. Choose based on how you prefer to see and understand the information.</p>
        </div>
      </div>
    </div>
  );
  
  // Step 1: Review Detected Issues
  const renderStep1 = () => (
    <div className="space-y-6" data-testid="wizard-step-1">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-2">
          Review the reporting issues we found
        </h2>
        <p className="text-stone-600 max-w-2xl mx-auto">
          We found reporting issues that may support a dispute. Review the items below and confirm what you want included in your dispute round.
        </p>
      </div>
      
      {analyzing ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mb-4" />
          <p className="text-stone-600">Analyzing your credit report...</p>
        </div>
      ) : (
        <>
          {/* Selection controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={clearAll}>
                Clear All
              </Button>
            </div>
            <div className="text-sm text-stone-600">
              {totalSelected} items selected • {packageCount} bureau package{packageCount !== 1 ? 's' : ''}
            </div>
          </div>
          
          {/* Negative Accounts */}
          {negativeAccounts.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                <CreditCard size={18} className="text-stone-500" />
                Negative Accounts ({negativeAccounts.length})
              </h3>
              <div className="grid gap-3">
                {negativeAccounts.map(account => (
                  <IssueCard
                    key={account.id}
                    item={account}
                    type="account"
                    isSelected={selectedItems.accounts.includes(account.id)}
                    onToggle={() => toggleItem('account', account.id)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Hard Inquiries */}
          {hardInquiries.length > 0 && (
            <div className="space-y-3 mt-6">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                <Search size={18} className="text-stone-500" />
                Hard Inquiries ({hardInquiries.length})
              </h3>
              <div className="grid gap-3">
                {hardInquiries.map(inquiry => (
                  <IssueCard
                    key={inquiry.id}
                    item={inquiry}
                    type="inquiry"
                    isSelected={selectedItems.inquiries.includes(inquiry.id)}
                    onToggle={() => toggleItem('inquiry', inquiry.id)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {negativeAccounts.length === 0 && hardInquiries.length === 0 && (
            <div className="text-center py-12 bg-stone-50 rounded-xl">
              <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">
                No Negative Items Found
              </h3>
              <p className="text-stone-600 mb-4">
                Your credit report doesn't have any negative items to dispute.
              </p>
              <Button onClick={() => navigate('/credit-report')}>
                View Credit Report
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
  
  // Step 2: Add Dispute Context
  const renderStep2 = () => (
    <div className="space-y-6" data-testid="wizard-step-2">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-2">
          Add Dispute Context
        </h2>
        <p className="text-stone-600 max-w-2xl mx-auto">
          Your dispute letter will be built from the reporting issues detected, the accounts selected, and the details you provide below.
        </p>
      </div>
      
      {/* Selected items summary */}
      <div className="bg-stone-50 rounded-xl p-4 mb-6">
        <h3 className="font-medium text-stone-900 mb-3">Selected Items</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(bureauCounts).map(([bureau, count]) => (
            <Badge key={bureau} variant="outline" className={BUREAU_COLORS[bureau]}>
              {bureau}: {count} item{count !== 1 ? 's' : ''}
            </Badge>
          ))}
        </div>
      </div>
      
      {/* Custom note */}
      <div className="space-y-2">
        <Label htmlFor="customNote" className="text-stone-700 font-medium">
          Additional Notes (Optional)
        </Label>
        <Textarea
          id="customNote"
          placeholder="Add any additional context about your dispute..."
          value={userContext.customNote}
          onChange={(e) => setUserContext(prev => ({ ...prev, customNote: e.target.value }))}
          className="min-h-[100px]"
          data-testid="dispute-custom-note"
        />
        <p className="text-xs text-stone-500">
          You can add personal context, but the system will build your dispute arguments automatically based on the detected issues.
        </p>
      </div>
      
      {/* Personal info correction */}
      <div className="space-y-2">
        <Label htmlFor="personalCorrection" className="text-stone-700 font-medium">
          Personal Information Correction (Optional)
        </Label>
        <Textarea
          id="personalCorrection"
          placeholder="If any personal information (name, address, SSN, etc.) is incorrect on your report, describe the correction needed..."
          value={userContext.personalInfoCorrection}
          onChange={(e) => setUserContext(prev => ({ ...prev, personalInfoCorrection: e.target.value }))}
          className="min-h-[80px]"
          data-testid="dispute-personal-correction"
        />
      </div>
      
      {/* Info box */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
        <Info size={20} className="text-emerald-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-emerald-800">
          <p className="font-medium mb-1">You don't need to write the dispute letter</p>
          <p>Our system will automatically generate a strong, legally-grounded dispute letter based on the Metro 2 reporting issues detected in your credit report.</p>
        </div>
      </div>
    </div>
  );
  
  // Step 3: Summary Before Generation
  const renderStep3 = () => (
    <div className="space-y-6" data-testid="wizard-step-3">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-2">
          Review Before Generating
        </h2>
        <p className="text-stone-600 max-w-2xl mx-auto">
          Confirm your selections before we generate your dispute letters.
        </p>
      </div>
      
      {/* Summary cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-stone-50 rounded-xl p-5 text-center">
          <div className="text-3xl font-bold text-emerald-600 mb-1">{totalSelected}</div>
          <div className="text-sm text-stone-600">Items Selected</div>
        </div>
        <div className="bg-stone-50 rounded-xl p-5 text-center">
          <div className="text-3xl font-bold text-emerald-600 mb-1">{packageCount}</div>
          <div className="text-sm text-stone-600">Bureau Packages</div>
        </div>
        <div className="bg-stone-50 rounded-xl p-5 text-center">
          <div className="text-3xl font-bold text-amber-600 mb-1">Standard</div>
          <div className="text-sm text-stone-600">Template</div>
        </div>
      </div>
      
      {/* Bureau breakdown */}
      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
          <Building2 size={18} />
          Letters to Generate
        </h3>
        <div className="space-y-3">
          {Object.entries(bureauCounts).map(([bureau, count]) => (
            <div key={bureau} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Badge className={BUREAU_COLORS[bureau]}>{bureau}</Badge>
                <span className="text-stone-700">{count} item{count !== 1 ? 's' : ''}</span>
              </div>
              <span className="text-sm text-stone-500">1 letter</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Legal explainer */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
        <h4 className="font-medium text-emerald-900 mb-2">What Your Letter Will Request</h4>
        <p className="text-sm text-emerald-800 leading-relaxed">
          Your dispute letter will be built from the reporting issues detected, the accounts selected, your dispute strategy, and any additional details you provide. The final letter will request a reasonable reinvestigation, review of all relevant information, and correction or deletion of any information that is inaccurate, incomplete, internally inconsistent, or cannot be verified.
        </p>
      </div>
    </div>
  );
  
  // Step 4: Generated Preview
  const renderStep4 = () => (
    <div className="space-y-6" data-testid="wizard-step-4">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-2">
          Your Dispute Letters
        </h2>
        <p className="text-stone-600 max-w-2xl mx-auto">
          {generatedLetters.length > 0 
            ? 'Your dispute letters have been generated. Review the preview below.'
            : 'Generate your dispute letters to preview them.'}
        </p>
      </div>
      
      {/* Round info */}
      {roundData && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle size={20} />
            <span className="font-medium">Round created with {roundData.findings_count || 0} findings</span>
          </div>
        </div>
      )}
      
      {/* Generate button if not yet generated */}
      {generatedLetters.length === 0 && (
        <div className="text-center py-8">
          <Button
            size="lg"
            onClick={handleGenerateLetters}
            disabled={loading || !roundData}
            className="btn-primary"
            data-testid="generate-letters-btn"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin mr-2" />
                Generating Letters...
              </>
            ) : (
              <>
                <FileText size={20} className="mr-2" />
                Generate Dispute Letters
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Letter preview */}
      {generatedLetters.length > 0 && (
        <div className="space-y-4">
          {/* Package tabs */}
          <div className="flex gap-2 flex-wrap">
            {generatedLetters.map((pkg, index) => (
              <Badge
                key={pkg.package_id}
                className={`cursor-pointer ${BUREAU_COLORS[pkg.target_bureau] || 'bg-stone-100'}`}
              >
                {pkg.target_bureau || `Package ${index + 1}`}
              </Badge>
            ))}
          </div>
          
          {/* Letter content preview */}
          {letterContent?.content && (
            <div className="border rounded-xl overflow-hidden">
              <div className="bg-stone-100 px-4 py-3 flex items-center justify-between">
                <span className="font-medium text-stone-700">Letter Preview</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(generatedLetters[0])}
                  >
                    <Download size={16} className="mr-1" />
                    Download
                  </Button>
                </div>
              </div>
              <div 
                className="p-6 bg-white max-h-[500px] overflow-y-auto prose prose-sm"
                dangerouslySetInnerHTML={{ __html: letterContent.content }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  // Step 5: Finalize / Download / Status
  const renderStep5 = () => (
    <div className="space-y-6" data-testid="wizard-step-5">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-stone-900 mb-2">
          Dispute Round Complete
        </h2>
        <p className="text-stone-600 max-w-2xl mx-auto">
          Your dispute letters are ready. Download them and send to the credit bureaus.
        </p>
      </div>
      
      {/* Status summary */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
          <div className="text-2xl font-bold text-emerald-700 mb-1">{roundData?.round_id?.slice(0, 8)}</div>
          <div className="text-sm text-emerald-600">Round ID</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
          <div className="text-2xl font-bold text-emerald-700 mb-1">{generatedLetters.length}</div>
          <div className="text-sm text-emerald-600">Letters Generated</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
          <Badge className="bg-emerald-100 text-emerald-700">Letters Ready</Badge>
          <div className="text-sm text-amber-600 mt-2">Status</div>
        </div>
      </div>
      
      {/* Download section */}
      <div className="bg-white border rounded-xl p-6">
        <h3 className="font-semibold text-stone-900 mb-4">Download Your Letters</h3>
        <div className="space-y-3">
          {generatedLetters.map((pkg, index) => (
            <div key={pkg.package_id} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText size={20} className="text-stone-500" />
                <div>
                  <p className="font-medium text-stone-900">{pkg.target_bureau || `Package ${index + 1}`}</p>
                  <p className="text-sm text-stone-500">Standard Template • {formatDate(new Date())}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownload(pkg)}
                data-testid={`download-letter-${pkg.package_id}`}
              >
                <Download size={16} className="mr-1" />
                Download
              </Button>
            </div>
          ))}
        </div>
      </div>
      
      {/* Next steps */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h4 className="font-medium text-amber-900 mb-2">Next Steps</h4>
        <ol className="text-sm text-amber-800 space-y-2 list-decimal list-inside">
          <li>Download your dispute letter(s)</li>
          <li>Print each letter and sign it</li>
          <li>Include copies of your ID and proof of address</li>
          <li>Send via certified mail with return receipt</li>
          <li>{t('disputes.trackInDisputeCenter')}</li>
        </ol>
      </div>
      
      {/* Actions */}
      <div className="flex justify-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/disputes')}
        >
          {t('disputes.goToDisputeCenter')}
        </Button>
        <Button
          onClick={() => navigate(`/disputes/${roundData?.round_id}`)}
          className="btn-primary"
          data-testid="view-round-btn"
        >
          View Round Details
        </Button>
      </div>
    </div>
  );
  
  // Determine if next button should be disabled
  const isNextDisabled = () => {
    if (loading) return true;
    
    // Adjust for step numbering
    const effectiveStep = disputeType ? currentStep : currentStep;
    
    if (effectiveStep === 0) {
      return !disputeType;
    }
    
    switch (disputeType ? currentStep : currentStep - 1) {
      case 1:
        return totalSelected === 0;
      case 2:
        return false;
      case 3:
        return false;
      case 4:
        return generatedLetters.length === 0;
      default:
        return false;
    }
  };
  
  return (
    <div className="min-h-screen bg-stone-50 py-8" data-testid="dispute-wizard-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/disputes')}
            className="mb-4"
          >
            <ChevronLeft size={20} className="mr-1" />
            Back to Disputes
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-stone-900">Create Dispute Round</h1>
            {disputeTypeInfo && (
              <Badge className={`${
                disputeType === 'factual' 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-emerald-100 text-emerald-700'
              }`}>
                {disputeTypeInfo.name}
              </Badge>
            )}
          </div>
          <p className="text-stone-600 mt-1">Auto Starter • Guided Workflow</p>
        </div>
        
        {/* Step indicator */}
        <StepIndicator
          currentStep={disputeType ? currentStep : currentStep + 1}
          totalSteps={totalSteps}
          stepLabels={stepLabels}
        />
        
        {/* Step content */}
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          {renderStepContent()}
        </div>
        
        {/* Navigation buttons */}
        {(disputeType ? currentStep < 5 : currentStep < 6) && (
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={goBack}
              disabled={currentStep === 0 || (disputeType && currentStep === 1) || loading}
            >
              <ChevronLeft size={20} className="mr-1" />
              Back
            </Button>
            
            {/* Finalize button for preview step */}
            {((disputeType && currentStep === 4) || (!disputeType && currentStep === 5)) && generatedLetters.length > 0 ? (
              <Button
                onClick={handleFinalize}
                disabled={loading}
                className="btn-primary"
                data-testid="finalize-btn"
              >
                Finalize
                <ChevronRight size={20} className="ml-1" />
              </Button>
            ) : ((disputeType && currentStep < 4) || (!disputeType && currentStep < 5)) ? (
              <Button
                onClick={goNext}
                disabled={isNextDisabled()}
                className="btn-primary"
                data-testid="next-step-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    {(disputeType ? currentStep === 2 : currentStep === 3) ? 'Create Round' : 'Continue'}
                    <ChevronRight size={20} className="ml-1" />
                  </>
                )}
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputeWizardPage;
